import { Col, DatePicker, Pagination, Row, Tag } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { IoMdNotifications } from "react-icons/io";
import { MdGroups, MdPeople, MdSchedule, MdSend, MdStorefront } from "react-icons/md";
import useMedia from "use-media";

import { Box, Button, Modal, Table, Text, TextField } from "../../components";
import { Colors, Fonts, Images, Utils } from "../../config";
import { fetchNotifications, sendPushNotification } from "./notificationApi";
import "./style.css";

const AUDIENCE_OPTIONS = [
  {
    value: "BOTH",
    label: "Users & Providers",
    hint: "Everyone",
    icon: MdGroups,
  },
  {
    value: "SU",
    label: "Service Users",
    hint: "Customers",
    icon: MdPeople,
  },
  {
    value: "SP",
    label: "Service Providers",
    hint: "Businesses",
    icon: MdStorefront,
  },
];

const TITLE_MAX = 25;
const MESSAGE_MAX = 150;

const emptyForm = {
  title: "",
  message: "",
  audience: "BOTH",
  sendNow: true,
  scheduledAt: null,
};

const emptyError = {
  title: "",
  message: "",
  audience: "",
  scheduledAt: "",
};

const audienceLabel = (value) =>
  AUDIENCE_OPTIONS.find((item) => item.value === value)?.label || value || "-";

const isScheduledNotification = (item) => {
  const status = String(item?.status || "").toLowerCase();
  if (status === "scheduled") return true;
  return Boolean(item?.scheduledAt) && !item?.sentAt;
};

const notificationStatus = (item) => {
  if (item?.status) return item.status;
  return isScheduledNotification(item) ? "Scheduled" : "Sent";
};

const tableStatusLabel = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "scheduled") return "Sched.";
  return status;
};

const UK_TIMEZONE = "Europe/London";

/** Current date/time as UK (Europe/London) wall clock, for schedule limits. */
const getUkNow = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: UK_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date());

  const get = (type) => parts.find((part) => part.type === type)?.value || "00";
  const hour = get("hour") === "24" ? "00" : get("hour");

  return moment(
    `${get("year")}-${get("month")}-${get("day")} ${hour}:${get("minute")}:${get("second")}`,
    "YYYY-MM-DD HH:mm:ss"
  );
};

/** Interpret picker wall-clock as UK time and return ISO (UTC). */
const ukWallToIso = (pickerValue) => {
  const wall = moment(pickerValue).format("YYYY-MM-DD HH:mm:ss");
  const wallAsUtcMs = Date.parse(wall.replace(" ", "T") + "Z");

  const formatUtcInUk = (ms) => {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: UK_TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(new Date(ms));
    const get = (type) => parts.find((part) => part.type === type)?.value || "00";
    const hour = get("hour") === "24" ? "00" : get("hour");
    return `${get("year")}-${get("month")}-${get("day")} ${hour}:${get("minute")}:${get("second")}`;
  };

  let utcMs = wallAsUtcMs;
  for (let i = 0; i < 2; i += 1) {
    const shownMs = Date.parse(formatUtcInUk(utcMs).replace(" ", "T") + "Z");
    utcMs += wallAsUtcMs - shownMs;
  }

  return new Date(utcMs).toISOString();
};

const formatDateTime = (value) =>
  value
    ? moment(value).calendar(null, {
        sameDay: "[Today] [at] hh:mm a",
        nextDay: "[Tomorrow] [at] hh:mm a",
        lastDay: "[Yesterday] [at] hh:mm a",
        sameElse: "DD MMMM [at] hh:mm a",
      })
    : "-";

const formatTableDateTime = (value) =>
  value
    ? moment(value).calendar(null, {
        sameDay: "[Today], hh:mm a",
        nextDay: "[Tomorrow], hh:mm a",
        lastDay: "[Yesterday], hh:mm a",
        sameElse: "DD MMM, hh:mm a",
      })
    : "-";

const disabledScheduleDate = (current) => {
  if (!current) return false;
  // Compare calendar days against UK "today" so past UK days stay disabled
  return current.format("YYYY-MM-DD") < getUkNow().format("YYYY-MM-DD");
};

const disabledScheduleTime = (date) => {
  if (!date) return {};

  const ukNow = getUkNow();
  if (date.format("YYYY-MM-DD") !== ukNow.format("YYYY-MM-DD")) {
    return {};
  }

  return {
    disabledHours: () => [...Array(ukNow.hour()).keys()],
    disabledMinutes: (selectedHour) =>
      selectedHour <= ukNow.hour() ? [...Array(ukNow.minute() + 1).keys()] : [],
  };
};

function NotificationManagement() {
  const isTablet = useMedia({ minWidth: "768px" });

  const [showCompose, setShowCompose] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState(emptyError);
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [state, setState] = useState({
    limit: 10,
    offset: 0,
  });

  const loadNotifications = async () => {
    setIsLoading(true);
    try {
      const res = await fetchNotifications(state);
      if (res?.status === 200) {
        setNotifications(res?.data?.data?.notifications || []);
        setTotal(res?.data?.data?.count || 0);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const resetForm = () => {
    setForm(emptyForm);
    setError(emptyError);
  };

  const validateForm = () => {
    const title = form.title.trim();
    const message = form.message.trim();

    if (!title) {
      setError({ ...emptyError, title: "Title is required" });
      return false;
    }
    if (title.length < 3 || title.length > TITLE_MAX) {
      setError({
        ...emptyError,
        title: `Title must be between 3 and ${TITLE_MAX} characters`,
      });
      return false;
    }
    if (!form.audience) {
      setError({ ...emptyError, audience: "Please select an audience" });
      return false;
    }
    if (!message) {
      setError({ ...emptyError, message: "Message is required" });
      return false;
    }
    if (message.length < 10 || message.length > MESSAGE_MAX) {
      setError({
        ...emptyError,
        message: `Message must be between 10 and ${MESSAGE_MAX} characters`,
      });
      return false;
    }
    if (!form.sendNow) {
      if (!form.scheduledAt) {
        setError({ ...emptyError, scheduledAt: "Please select a date and time" });
        return false;
      }
      // Treat the picked wall clock as UK time when checking "in the future"
      const selectedUk = moment(
        moment(form.scheduledAt).format("YYYY-MM-DD HH:mm:ss"),
        "YYYY-MM-DD HH:mm:ss"
      );
      if (selectedUk.isSameOrBefore(getUkNow())) {
        setError({
          ...emptyError,
          scheduledAt: "Scheduled time must be in the future (UK time)",
        });
        return false;
      }
    }

    setError(emptyError);
    return true;
  };

  const handleOpenConfirm = () => {
    if (validateForm()) {
      setShowConfirm(true);
    }
  };

  const handleSend = async () => {
    setIsSending(true);
    try {
      const payload = {
        title: form.title.trim(),
        message: form.message.trim(),
        audience: form.audience,
        sendType: form.sendNow ? "now" : "schedule",
        scheduledAt: form.sendNow ? null : ukWallToIso(form.scheduledAt),
      };
      const res = await sendPushNotification(payload);

      if (res?.status === 200) {
        Utils.showSnackBar({
          message:
            res?.data?.data?.message ||
            res?.data?.message ||
            (form.sendNow
              ? "Notification sent successfully"
              : "Notification scheduled successfully"),
        });
        setShowConfirm(false);
        setShowCompose(false);
        resetForm();
        setPage(1);
        setState((prev) => ({ ...prev, offset: 0 }));
      } else {
        Utils.showSnackBar({
          message: res?.data?.error?.message || "Failed to send notification",
          severity: "error",
        });
      }
    } catch (err) {
      Utils.showSnackBar({
        message: err?.message || "Failed to send notification",
        severity: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handlePaginationChange = (pageNumber) => {
    setPage(pageNumber);
    setState({
      ...state,
      offset: (pageNumber - 1) * state.limit,
    });
  };

  const scheduledPreview = !form.sendNow && form.scheduledAt
    ? moment(form.scheduledAt).format("DD MMM, hh:mm a")
    : "now";

  return (
    <>
      <Box className="notification-page">
        <Row justify="space-between" align="middle" className="notification-page-header" gutter={[12, 12]}>
          <Col className="flex-row-start">
            <Text.Heading
              fontSize={isTablet ? 24 : 20}
              fontFamily={Fonts["Poppins-SemiBold"]}
              color={Colors.Black}
              fontWeight={600}
              text={"Notification Management"}
            />
          </Col>
          <Col className="notification-toolbar">
            <div className="notification-toolbar-row">
              <Button.Basic
                onClick={() => setShowCompose(true)}
                text={"Send Notification"}
                startIcon={<IoMdNotifications color="white" />}
                className="send-notification-btn"
              />
            </div>
          </Col>
        </Row>

        <Row justify="space-between" className="mt-20">
          <Col span={24}>
            <Table.AntDesignTable
              className="notifications-table"
              loading={isLoading}
              columns={[
                {
                  title: "Title",
                  dataIndex: "title",
                  key: "title",
                  width: 150,
                  className: "notification-title-col",
                  ellipsis: true,
                  render: (text) => {
                    const title = text || "-";
                    const display =
                      title !== "-" && title.length > 20
                        ? `${title.slice(0, 20)}...`
                        : title;
                    return (
                      <span
                        className="notification-title-cell"
                        title={title !== "-" ? title : undefined}
                      >
                        {display}
                      </span>
                    );
                  },
                },
                {
                  title: "Message",
                  dataIndex: "message",
                  key: "message",
                  className: "notification-message-col",
                  ellipsis: true,
                  render: (text) => {
                    const message = text || "-";
                    return (
                      <span
                        className="notification-message-cell"
                        title={message !== "-" ? message : undefined}
                      >
                        {message}
                      </span>
                    );
                  },
                },
                {
                  title: "Audience",
                  dataIndex: "audience",
                  key: "audience",
                  width: 140,
                  className: "notification-audience-col",
                  render: (value) => (
                    <Tag color="#FAB700" className="audience-tag">
                      {audienceLabel(value)}
                    </Tag>
                  ),
                },
                {
                  title: "Scheduled At",
                  dataIndex: "scheduledAt",
                  key: "scheduledAt",
                  width: 112,
                  className: "notification-date-col",
                  render: (value) => (
                    <span className="notification-datetime-cell">
                      {value ? formatTableDateTime(value) : "-"}
                    </span>
                  ),
                },
                {
                  title: "Sent At",
                  key: "sentAt",
                  width: 112,
                  className: "notification-sent-col",
                  render: (_, record) => {
                    const sentAt =
                      record.sentAt ||
                      (isScheduledNotification(record) ? null : record.createdAt);
                    return (
                      <span className="notification-datetime-cell">
                        {sentAt ? formatTableDateTime(sentAt) : "-"}
                      </span>
                    );
                  },
                },
                {
                  title: "Receipt Count",
                  dataIndex: "recipientCount",
                  key: "recipientCount",
                  width: 72,
                  className: "notification-receipt-col",
                  render: (value) => (
                    <span className="notification-count-cell">
                      {value || value === 0 ? value : "-"}
                    </span>
                  ),
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  width: 64,
                  className: "notification-status-col",
                  align: "center",
                  render: (value, record) => {
                    const status = notificationStatus({ ...record, status: value });
                    const scheduled = String(status).toLowerCase() === "scheduled";
                    return (
                      <span
                        className={
                          scheduled
                            ? "notification-status scheduled"
                            : "notification-status sent"
                        }
                      >
                        {tableStatusLabel(status)}
                      </span>
                    );
                  },
                },
                {
                  title: "",
                  key: "action",
                  width: 48,
                  className: "notification-action-col",
                  align: "center",
                  render: (_, record) => (
                    <img
                      src={Images.ViewIcon}
                      alt="view"
                      width="32"
                      className="notification-view-icon"
                      onClick={() => {
                        setSelectedNotification(record);
                        setShowDetails(true);
                      }}
                    />
                  ),
                },
              ]}
              data={notifications}
              pagination={false}
              rowKey="id"
            />
            {!isLoading && notifications.length === 0 && (
              <div className="notification-empty">
                No notifications yet. Send one now or schedule it for later.
              </div>
            )}
            <Row justify="center" className="mt-20">
              <Col>
                <Pagination
                  responsive="true"
                  showLessItems="true"
                  pageSize={state.limit}
                  current={page}
                  onChange={handlePaginationChange}
                  total={total}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Box>

      {showCompose && (
        <Modal.AntDesignModal
          className="ant-modal notification-compose-modal"
          centered={true}
          title=""
          isModalVisible={showCompose}
          handleCancel={() => {
            setShowCompose(false);
            resetForm();
          }}
        >
          <Row justify="center">
            <Col>
              <Text.Heading
                fontSize={26}
                fontFamily={Fonts["Poppins-SemiBold"]}
                color={Colors.Black}
                fontWeight={600}
                text={"Send Notification"}
              />
            </Col>
          </Row>
          <Row justify="center" className="mt-10">
            <Col span={24}>
              <Text.Heading
                fontSize={16}
                fontFamily={Fonts["Poppins-Regular"]}
                color={Colors.LightGrayText}
                fontWeight={400}
                text={"Send updates, announcements, or promotions to a selected audience."}
              />
            </Col>
          </Row>

          <Row justify="center" className="mt-20">
            <Col span={24}>
              <Text.Heading
                fontSize={18}
                fontFamily={Fonts["Poppins-Regular"]}
                color={Colors.TextColor}
                fontWeight={400}
                text={"Target Audience:"}
                textAlign="left"
              />
              <div className="audience-grid mt-10">
                {AUDIENCE_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const selected = form.audience === option.value;
                  return (
                    <div
                      key={option.value}
                      className={`audience-card ${selected ? "selected" : ""}`}
                      onClick={() => setForm({ ...form, audience: option.value })}
                    >
                      <Icon size={22} color={selected ? "#FAB700" : "#8E8E8E"} />
                      <span className="audience-card-label">
                        {option.value === "SU" ? (
                          <>
                            Service
                            <br />
                            Users
                          </>
                        ) : (
                          option.label
                        )}
                      </span>
                      <span className="audience-card-hint">{option.hint}</span>
                    </div>
                  );
                })}
              </div>
              <Text.Heading
                fontSize={16}
                fontFamily={Fonts["Poppins-Regular"]}
                color={"red"}
                fontWeight={400}
                text={error.audience}
                textAlign="left"
              />
            </Col>
          </Row>

          <Row justify="center" className="mt-20">
            <Col span={24}>
              <TextField.CustomInput
                label="Title: "
                placeholder="Notification title"
                value={form.title}
                size="large"
                type="text"
                maxLength={TITLE_MAX}
                className="sub-admin-input"
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
              <div className="char-count">
                {form.title.length}/{TITLE_MAX}
              </div>
              <Text.Heading
                fontSize={16}
                fontFamily={Fonts["Poppins-Regular"]}
                color={"red"}
                fontWeight={400}
                text={error.title}
                textAlign="center"
              />
            </Col>
          </Row>

          <Row justify="center" className="mt-10 flex-col">
            <Col span={24}>
              <Text.Heading
                fontSize={18}
                fontFamily={Fonts["Poppins-Regular"]}
                color={Colors.TextColor}
                fontWeight={400}
                text={"Message:"}
                textAlign="left"
              />
              <TextField.Textarea
                value={form.message}
                placeholder={"Write the notification message"}
                rows={5}
                maxLength={MESSAGE_MAX}
                handleChange={(e) => setForm({ ...form, message: e.target.value })}
              />
              <div className="char-count">
                {form.message.length}/{MESSAGE_MAX}
              </div>
              <Text.Heading
                fontSize={16}
                fontFamily={Fonts["Poppins-Regular"]}
                color={"red"}
                fontWeight={400}
                text={error.message}
                textAlign="center"
                className="mb-10"
              />
            </Col>
          </Row>

          <Row justify="center" className="mt-10">
            <Col span={24}>
              <Text.Heading
                fontSize={18}
                fontFamily={Fonts["Poppins-Regular"]}
                color={Colors.TextColor}
                fontWeight={400}
                text={"Delivery:"}
                textAlign="left"
              />
              <div className="delivery-grid mt-10">
                <div
                  className={`audience-card ${form.sendNow ? "selected" : ""}`}
                  onClick={() =>
                    setForm({ ...form, sendNow: true, scheduledAt: null })
                  }
                >
                  <MdSend size={22} color={form.sendNow ? "#FAB700" : "#8E8E8E"} />
                  <span className="audience-card-label">Send now</span>
                  <span className="audience-card-hint">Goes out immediately</span>
                </div>
                <div
                  className={`audience-card ${!form.sendNow ? "selected" : ""}`}
                  onClick={() => setForm({ ...form, sendNow: false })}
                >
                  <MdSchedule size={22} color={!form.sendNow ? "#FAB700" : "#8E8E8E"} />
                  <span className="audience-card-label">Schedule for later</span>
                  <span className="audience-card-hint">Pick a date and time</span>
                </div>
              </div>
              {!form.sendNow && (
                <>
                  <DatePicker
                    className="schedule-picker mt-10"
                    value={form.scheduledAt}
                    onChange={(value) => setForm({ ...form, scheduledAt: value })}
                    showTime={{ format: "hh:mm A", use12Hours: true }}
                    format="DD MMM YYYY, hh:mm A"
                    placeholder="Select date and time (UK)"
                    disabledDate={disabledScheduleDate}
                    disabledTime={disabledScheduleTime}
                    showNow={false}
                    allowClear={false}
                    style={{ width: "100%" }}
                  />
                  <div className="schedule-tz-hint">Times use UK time (GMT/BST)</div>
                </>
              )}
              <Text.Heading
                fontSize={16}
                fontFamily={Fonts["Poppins-Regular"]}
                color={"red"}
                fontWeight={400}
                text={error.scheduledAt}
                textAlign="left"
              />
            </Col>
          </Row>

          <Row justify="center" className="mt-10">
            <Col span={24}>
              <Text.Heading
                fontSize={18}
                fontFamily={Fonts["Poppins-Regular"]}
                color={Colors.TextColor}
                fontWeight={400}
                text={"Preview:"}
                textAlign="left"
              />
              <div className="notification-preview">
                <div className="notification-preview-app">
                  Mobrage • {scheduledPreview}
                </div>
                <div className="notification-preview-title">
                  {form.title.trim() || "Notification title"}
                </div>
                <div className="notification-preview-body">
                  {form.message.trim() || "Your message will appear here"}
                </div>
              </div>
            </Col>
          </Row>

          <Row justify="center">
            <Col span={24}>
              <Button.Basic
                text={
                  isSending
                    ? form.sendNow
                      ? "Sending..."
                      : "Scheduling..."
                    : form.sendNow
                    ? "Send Notification"
                    : "Schedule Notification"
                }
                onClick={handleOpenConfirm}
                className="sub-admin-add-btn mt-20"
                disabled={isSending}
              />
            </Col>
          </Row>
        </Modal.AntDesignModal>
      )}

      {showConfirm && (
        <Modal.ConfirmationModal
          title=""
          isModalVisible={showConfirm}
          heading={form.sendNow ? "Send this notification?" : "Schedule this notification?"}
          subHeading={
            form.sendNow
              ? `This will go to ${audienceLabel(form.audience)} in real time.`
              : `This will go to ${audienceLabel(form.audience)} on ${moment(
                  form.scheduledAt
                ).format("DD MMM YYYY [at] hh:mm a")}.`
          }
          handleCancel={() => setShowConfirm(false)}
          handleDelete={handleSend}
          style={{ maxWidth: "calc(100vw - 24px)" }}
        />
      )}

      {showDetails && selectedNotification && (
        <Modal.AntDesignModal
          className="ant-modal notification-details-modal"
          centered={true}
          title=""
          isModalVisible={showDetails}
          handleCancel={() => {
            setShowDetails(false);
            setSelectedNotification(null);
          }}
        >
          <Row justify="center">
            <Col>
              <Text.Heading
                fontSize={26}
                fontFamily={Fonts["Poppins-SemiBold"]}
                color={Colors.Black}
                fontWeight={600}
                text={"Notification Details"}
              />
            </Col>
          </Row>
          <Row className="mt-20">
            <Col span={24}>
              <div className="view-notification-label">Title</div>
              <div className="view-notification-value">{selectedNotification.title}</div>
            </Col>
          </Row>
          <Row className="mt-20">
            <Col span={24}>
              <div className="view-notification-label">Audience</div>
              <Tag color="#FAB700" className="audience-tag">
                {audienceLabel(selectedNotification.audience)}
              </Tag>
            </Col>
          </Row>
          <Row className="mt-20">
            <Col span={24}>
              <div className="view-notification-label">Message</div>
              <div className="view-notification-value">{selectedNotification.message}</div>
            </Col>
          </Row>
          <Row className="mt-20">
            <Col span={24}>
              <div className="view-notification-label">Status</div>
              <div className="view-notification-value">
                <span
                  className={
                    isScheduledNotification(selectedNotification)
                      ? "notification-status scheduled"
                      : "notification-status sent"
                  }
                >
                  {notificationStatus(selectedNotification)}
                </span>
              </div>
            </Col>
          </Row>
          <Row className="mt-20">
            <Col span={24}>
              <div className="view-notification-label">Scheduled At</div>
              <div className="view-notification-value">
                {selectedNotification.scheduledAt
                  ? formatDateTime(selectedNotification.scheduledAt)
                  : "-"}
              </div>
            </Col>
          </Row>
          <Row className="mt-20">
            <Col span={24}>
              <div className="view-notification-label">Sent At</div>
              <div className="view-notification-value">
                {formatDateTime(
                  selectedNotification.sentAt ||
                    (isScheduledNotification(selectedNotification)
                      ? null
                      : selectedNotification.createdAt)
                )}
              </div>
            </Col>
          </Row>
          <Row className="mt-20">
            <Col span={24}>
              <div className="view-notification-label">Receipt Count</div>
              <div className="view-notification-value">
                {selectedNotification.recipientCount ||
                selectedNotification.recipientCount === 0
                  ? selectedNotification.recipientCount
                  : "-"}
              </div>
            </Col>
          </Row>
        </Modal.AntDesignModal>
      )}
    </>
  );
}

export default NotificationManagement;
